import { getFieldLogic } from "@smartsuite/fields-logic/lib/field-type-registry/fields-logic-registry";
import debounce from "lodash.debounce";
import { Fields } from "../types/fields";
import {
  type FormConfigApplication,
  type FormConfigItem,
  FormItemType,
  type FormValues,
} from "../types/form";

// Set of helpers to store temporary progress of a user filling in a form, and then being
// able to restore that progress if they leave the page and come back to it.
// Includes logic to clean up the data stored in the local storage, to ensure that the data
// is valid for the form config.

/**
 * Returns the key to use for the local storage, unique for each form and account.
 * This ensures a user can hold progress of multiple different forms at the same time.
 * @param accountId The account ID from the URL
 * @param hash The form's hash from the URL
 * @returns The key to use for the local storage
 */
const getLocalStorageKey = (accountId: string, hash: string): string => {
  return `${accountId}_${hash}`;
};

export const clearCheckpoint = (accountId: string, hash: string): void => {
  localStorage.removeItem(getLocalStorageKey(accountId, hash));
};

const saveCheckpoint = (
  accountId: string,
  hash: string,
  values: FormValues,
  application?: FormConfigApplication,
): void => {
  const lookupSlugs = application
    ? new Set(
        application.structure.filter((f) => f.field_type === Fields.lookupfield).map((f) => f.slug),
      )
    : new Set<string>();

  const entries = Object.entries(values).filter(
    ([key, value]) =>
      !lookupSlugs.has(key) && value !== null && value !== undefined && value !== "",
  );
  const nonEmptyValues = Object.fromEntries(entries);

  localStorage.setItem(getLocalStorageKey(accountId, hash), JSON.stringify(nonEmptyValues));
};

// Increased debounce delay from 500ms to 1000ms to reduce frequent saves
export const saveCheckpointDebounced = debounce(saveCheckpoint, 1000);

export const loadCheckpoint = (
  accountId: string,
  hash: string,
  application: FormConfigApplication,
  formItems: FormConfigItem[],
): FormValues => {
  const formValues = localStorage.getItem(getLocalStorageKey(accountId, hash));
  if (formValues) {
    try {
      const parsed = JSON.parse(formValues);
      if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
        throw new Error("Checkpoint data is not a plain object");
      }
      return cleanupCheckpointData(parsed, application, formItems);
    } catch (error) {
      console.warn("Failed to restore form progress from localStorage, starting fresh.", error);
      clearCheckpoint(accountId, hash);
    }
  }
  return {};
};

const cleanupCheckpointData = (
  formValues: FormValues,
  application: FormConfigApplication,
  formItems: FormConfigItem[],
): FormValues => {
  const formFieldsFlatList = formItems
    .filter((formItem) =>
      [
        FormItemType.section,
        FormItemType.field,
        FormItemType.consent,
        FormItemType.pdf_viewer,
      ].includes(formItem.type),
    )
    .flatMap((formItem) => {
      return formItem.type === FormItemType.section
        ? formItem.params.items
            ?.filter((item) =>
              [FormItemType.field, FormItemType.consent, FormItemType.pdf_viewer].includes(
                item.type,
              ),
            )
            .map((item) => item)
        : [formItem];
    });

  const formItemsSlugMap = new Map(
    formFieldsFlatList.map((formItem) => [formItem?.slug, formItem]),
  );
  const appFieldsSlugMap = new Map(
    application.structure.map((appField) => [appField.slug, appField]),
  );

  const cleanedValues: FormValues = {};
  Object.keys(formValues).forEach((key) => {
    // Ignore all keys from the object that aren't in the form config
    if (!formItemsSlugMap.has(key)) return;

    // A read-only item's value can never be something the respondent typed, so a stored copy of it
    // is a stale snapshot of the config, not progress worth restoring. Skipping it lets the config
    // seed the item fresh on every visit — otherwise a "Today" default stays frozen on the day the
    // form was first opened, forever (BUG-21601). Also self-heals checkpoints already written.
    if (formItemsSlugMap.get(key)?.params.read_only) return;

    const fieldType = appFieldsSlugMap.get(key)?.field_type;
    if (fieldType === Fields.lookupfield) return;

    // Grab the field logic for each of the fields by its type. if not available, ignore the field
    const storedFieldValue = formValues[key];
    const fieldLogic = fieldType ? getFieldLogic(fieldType) : null;
    const formItemType = formItemsSlugMap.get(key)?.type;
    if (
      !fieldLogic &&
      formItemType !== FormItemType.consent &&
      formItemType !== FormItemType.pdf_viewer
    )
      return;

    // Otherwise, allow the field data to be loaded
    cleanedValues[key] = storedFieldValue;
  });

  return cleanedValues;
};
